import {
  Cell,
  Funnel,
  FunnelChart,
  LabelList,
  Legend,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { isEmpty } from "underscore";
import { useMemo } from "react";

import { callFunctionIfExists } from "../../utils/GeneralUtils";
import { isValidChartQuery } from "../../utils/DashboardUtils";
import { BaseChartProps, SelectedChart } from "./WidgetTypes";
import { IsGroupSelected } from "../../utils/ChartUtils";
import useChartData from "./WidgetHooks/useChartData";
import { Filter } from "../../types/GlobalInterfaces";
import { CustomLegendLabel } from "./PieChartWidget";
import ChartTooltip from "./Tooltips/ChartTooltip";
import useThemeColors from "./useThemeColors";
import useModule from "../../hooks/useModule";
import WidgetLoading from "./WidgetLoading";
import WidgetError from "./WidgetError";

export default function StageFunnelWidget({
  section,
  setSettings,
  record,
  theme,
  topLevelFilters,
  viewFilters,
  selectedGroup,
  onSelectGroup,
}: BaseChartProps) {
  const { module } = useModule(section?.ModuleAPIName);
  const {
    chartData,
    chartError,
    chartLoading,
    groupByField,
    groupByFieldType,
    groupByFieldName,
    aggregationMethod,
    aggregationFieldName,
    aggregationField,
  } = useChartData({
    moduleAPIName: section?.ModuleAPIName,
    query: section?.Query,
    validator: isValidChartQuery,
    orderBy: section?.OrderBy ?? null,
    record: record,
    topLevelFilters: topLevelFilters,
    viewFilters: viewFilters,
    queryKeyID: section?.id,
  });
  const { colors } = useThemeColors({ theme, chartData });
  const stageName = section?.Query?.GroupBy?.Field;

  const preparedData = useMemo(() => {
    if (!chartData || !Array.isArray(chartData)) return [];

    let finalData = [];

    const fields = section?.Query?.Fields?.filter((f: any) => f !== "*");

    // If fields were provided in the chart settings, filter out other fields
    if (fields?.length > 0) {
      const dataArray = chartData.map((d: any) => ({
        ...d,
        [stageName]: d[stageName] ? d[stageName] : null,
      }));

      finalData = fields?.flatMap((f: string) => {
        const foundItem = dataArray.find((d) => d[stageName] === f);

        return foundItem ? [foundItem] : [];
      });
    } else {
      // If no fields were provided, sort the fields in descending order to create a nice funnel appearance.
      finalData = chartData
        .flatMap((d: any) => {
          return [
            {
              ...d,
              [stageName]: d[stageName] ? d[stageName] : null,
            },
          ];
        })
        .sort((a: any, b: any) => {
          return b[aggregationMethod] === a[aggregationMethod]
            ? 0
            : b[aggregationMethod] < a[aggregationMethod]
            ? -1
            : 1;
        });
    }

    return finalData;
  }, [chartData, section]);

  if (chartError) {
    return (
      <WidgetError
        onOpenSettings={() => callFunctionIfExists(setSettings(section))}
      />
    );
  }

  if (chartLoading) {
    return <WidgetLoading />;
  }

  return (
    <ResponsiveContainer>
      <FunnelChart>
        <Tooltip
          content={
            <ChartTooltip
              aggregationField={aggregationField}
              aggregationMethod={aggregationMethod}
              groupByField={groupByField}
              groupByFieldName={groupByFieldName}
              aggregationFieldName={aggregationFieldName}
              moduleName={module?.Name ?? ""}
            />
          }
        />
        <Funnel
          nameKey={groupByField}
          dataKey={aggregationMethod}
          data={preparedData}
          isAnimationActive
          onClick={
            onSelectGroup
              ? (entry) => {
                  onSelectGroup({
                    group: [
                      {
                        Group: false,
                        groupByField,
                        groupByValue: isEmpty(entry?.[groupByField])
                          ? undefined
                          : entry?.[groupByField],
                      },
                    ],
                    filters: section?.Query?.Filters,
                  });
                }
              : undefined
          }
        >
          <LabelList position="right" stroke="none" dataKey="name" />
          {preparedData &&
            preparedData.map((entry: any, i: number) => {
              const isSelected = IsGroupSelected({
                selectedGroup,
                groupByField,
                groupValue: entry,
                groupByFieldType,
                rawGroupByField: section?.Query?.GroupBy?.Field,
              });

              const fill = colors?.[i];

              const finalFill =
                selectedGroup !== undefined ? `${fill}80` : fill;

              entry.fill = isSelected ? fill : finalFill;

              return (
                <Cell
                  key={`cell-${i}`}
                  cursor="pointer"
                  fill={isSelected ? fill : finalFill}
                  stroke={isSelected ? `${fill}80` : undefined}
                  strokeWidth={4}
                />
              );
            })}
        </Funnel>
        <Legend
          formatter={(data) =>
            CustomLegendLabel({
              data,
              value: data,
              groupByField,
              groupByFieldName,
              onSelectGroup,
              rawGroupByField: section?.Query?.GroupBy?.Field,
              filters: section?.Query?.Filters,
            })
          }
          iconSize={12}
          payload={preparedData}
          content={(props) => (
            <FunnelLegend
              props={props}
              groupByField={groupByField}
              onSelectGroup={onSelectGroup}
              groupByFieldName={groupByFieldName}
              filters={section?.Query?.Filters}
            />
          )}
        />
      </FunnelChart>
    </ResponsiveContainer>
  );
}

function FunnelLegend({
  props,
  groupByField,
  groupByFieldName,
  onSelectGroup,
  filters,
}: {
  props: any;
  groupByField: string;
  groupByFieldName: string;
  onSelectGroup?: (chartGroup: SelectedChart) => void;
  filters?: Filter[];
}) {
  const { payload } = props;

  return (
    <div className="mt--2 mb--3 p-0 d-flex flex-wrap justify-content-center text-xs font-weight-500">
      {payload.map((entry: any) => (
        <div
          key={entry?.[groupByField]}
          className={`d-flex align-items-center pb-1 ${
            onSelectGroup ? "cursor-pointer" : ""
          }`}
          style={{ paddingRight: 15, cursor: "pointer" }}
          onClick={() => {
            onSelectGroup &&
              onSelectGroup({
                group: [
                  {
                    Group: false,
                    groupByField: groupByField,
                    groupByValue: isEmpty(entry?.[groupByField])
                      ? undefined
                      : entry?.[groupByField],
                  },
                ],
                filters,
              });

            return undefined;
          }}
        >
          <div
            className="rounded-sm mr-1"
            style={{ backgroundColor: entry.fill, height: 15, width: 15 }}
          ></div>
          {entry?.[groupByField] || `No ${groupByFieldName}`}
        </div>
      ))}
    </div>
  );
}
